import { DrEntity } from './dr-entity';
import { DrNamedQuery } from './dr-named-query';
import { DrObject } from '../models/dr-object';
import { isNullOrUndefined } from './utils';

export enum Languages {
    GENERAL = 'General',
    ARABIC = 'Arabic',
    CYRILLIC = 'Cyrillic',
    ENGLISH = 'English',
    GREEK = 'Greek',
    HEBREW = 'Hebrew',
    THAI = 'Thai'
}

export enum IndexLevel {
    DO_NOT_INDEX = 'do-not-index',
    SYSTEM_METADATA = 'system-metadata',
    FILE_METADATA = 'file-metadata',
    CONTENT = 'content',
    ANALYTIC = 'analytic'
}

export enum DocStorageExclusions {
    DOCCLASS_DISKIMAGE = 'DOCCLASS_DISKIMAGE',
    DOCCLASS_ARCHIVE = 'DOCCLASS_ARCHIVE',
    DOCCLASS_MAIL_CONTAINER = 'DOCCLASS_MAIL_CONTAINER',
    NIST_EDOC = 'NIST_EDOC',
}

export class IndexSettings extends DrEntity {
    virusDetection: boolean;
    includeEmailHeaders: boolean;
    extractEmbeddedImages: boolean;
    ignoreTransportHeaders: boolean;
    includePageBreaks: boolean;
    splitChats: boolean;
    splitJournaledEmails: boolean;
    splitIndividualRSMFMessage: boolean;
    includeCurrency: boolean;
    includeNumericQuantities: boolean;
    includeNumericTerms: boolean;
    accentSensitive: boolean;
    languageDetectionProcessing: boolean;
    ocrProcessing: boolean;
    ocrAccuracyHigh: boolean;
    ocrPageTimeout: number;
    custodianDirectoryLocation: string;
    mediaDirectoryLocation: string;
    ocrCandidatesNamedQueries = new Array<DrNamedQuery>();
    customWarningsAndErrorNamedQueries = new Array<DrNamedQuery>();
    emailHeaderMappings: any = {};
    emailHeaderMappingsAsArray = new Array<DrNamedQuery>();
    matterName: string;
    matterNumber: string;
    copyToDocStorage: boolean;
    ocrLanguage: string;
    copyToDocStorageExclusions: Array<DocStorageExclusions>;
    exclusions: Array<DocStorageExclusions>;
    clusterWithStopWords: boolean;
    documentProcessingTimeout: any;
    archivalProcessingTimeout: any;
    externalParsingLibrary: string;
    externalParsingLibraryDisplay: string;
    embeddedImageWidth?: number;
    embeddedImageHeight?: number;
    extractionExclusions: any;
    extractCustomEmailHeaders: boolean;


    constructor(value?: Partial<IndexSettings>) {
        super(value);
        if (value) {
            if (value.ocrCandidatesNamedQueries) {
                value.ocrCandidatesNamedQueries = value.ocrCandidatesNamedQueries.map(namedQuery => new DrNamedQuery(namedQuery));
            }
            if (value.customWarningsAndErrorNamedQueries) {
                value.customWarningsAndErrorNamedQueries = value.customWarningsAndErrorNamedQueries.map(namedQuery => new DrNamedQuery(namedQuery));
            }

            if (value.externalParsingLibrary) {
                value.externalParsingLibraryDisplay = value.externalParsingLibrary === 'OIT' ? 'V1' : 'V2';
            }

            //value.emailHeaderMappings ={X_ZANTAZ_MAILBOXOWNER: 'drcustom_x_zantaz_mailboxowner'};

            Object.assign(this, value);

            this.ocrPageTimeout = this.ocrPageTimeout == null || this.ocrPageTimeout === 0 ? 120 : this.ocrPageTimeout;
        }
    }

    // TODO: namedqueries = multiple drobjects.
}

export class IndexSettingsViewModel {
    static emptyString = '';

    model: Partial<IndexSettings>;
    original: Partial<IndexSettings>;

    constructor(value?: Partial<IndexSettings>) {
        if (value) {
            this.model = value;

            if (isNullOrUndefined(this.model.custodianDirectoryLocation)) {
                this.model.custodianDirectoryLocation = IndexSettingsViewModel.emptyString;
            }
            if (isNullOrUndefined(this.model.mediaDirectoryLocation)) {
                this.model.mediaDirectoryLocation = IndexSettingsViewModel.emptyString;
            }

            if (isNullOrUndefined(this.model.matterName)) {
                this.model.matterName = IndexSettingsViewModel.emptyString;
            }

            if (isNullOrUndefined(this.model.matterNumber)) {
                this.model.matterNumber = IndexSettingsViewModel.emptyString;
            }
            if (isNullOrUndefined(this.model.extractionExclusions)) {
                this.model.extractionExclusions = [];
            }

            if (isNullOrUndefined(this.model.embeddedImageWidth)) {
                this.model.embeddedImageWidth = 0;
            } else {
                this.model.embeddedImageWidth = this.model.embeddedImageWidth
            }

            if (isNullOrUndefined(this.model.embeddedImageHeight)) {
                this.model.embeddedImageHeight = 0;
            } else {
                this.model.embeddedImageHeight = this.model.embeddedImageHeight;
            }
        }

        if (this.model) {
            this.original = new IndexSettings(this.model);
        }
    }

    isDirty(): boolean {
        const dirty = false;
        if (this.model.virusDetection !== this.original.virusDetection) {
            return true;
        }

        if (this.model.includeEmailHeaders !== this.original.includeEmailHeaders) {
            return true;
        }

        if (this.model.extractEmbeddedImages !== this.original.extractEmbeddedImages) {
            return true;
        }

        if (this.model.ignoreTransportHeaders !== this.original.ignoreTransportHeaders) {
            return true;
        }

        if (this.model.includePageBreaks !== this.original.includePageBreaks) {
            return true;
        }

        if (this.model.splitChats !== this.original.splitChats) {
            return true;
        }

        if (this.model.splitJournaledEmails !== this.original.splitJournaledEmails) {
            return true;
        }

        if (this.model.splitIndividualRSMFMessage !== this.original.splitIndividualRSMFMessage) {
            return true;
        }

        if (this.model.includeCurrency !== this.original.includeCurrency) {
            return true;
        }

        if (this.model.includeNumericQuantities !== this.original.includeNumericQuantities) {
            return true;
        }

        if (this.model.includeNumericTerms !== this.original.includeNumericTerms) {
            return true;
        }

        if (this.model.accentSensitive !== this.original.accentSensitive) {
            return true;
        }

        if (this.model.languageDetectionProcessing !== this.original.languageDetectionProcessing) {
            return true;
        }

        if (this.model.ocrProcessing !== this.original.ocrProcessing) {
            return true;
        }

        if (this.model.ocrAccuracyHigh !== this.original.ocrAccuracyHigh) {
            return true;
        }

        if (this.model.ocrPageTimeout !== this.original.ocrPageTimeout) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.custodianDirectoryLocation, this.original.custodianDirectoryLocation)) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.mediaDirectoryLocation, this.original.mediaDirectoryLocation)) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.matterName, this.original.matterName)) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.matterNumber, this.original.matterNumber)) {
            return true;
        }

        if (this.model.copyToDocStorage !== this.original.copyToDocStorage) {
            return true;
        }

        if (DrObject.isValueDirty(this.model.ocrLanguage, this.original.ocrLanguage)) {
            return true;
        }

        if (this.model.clusterWithStopWords !== this.original.clusterWithStopWords) {
            return true;
        }

        if (this.model.documentProcessingTimeout?.toString() !== this.original.documentProcessingTimeout?.toString()) {
            return true;
        }

        if (this.model.archivalProcessingTimeout?.toString() !== this.original.archivalProcessingTimeout?.toString()) {
            return true;
        }

        if (this.model.extractEmbeddedImages !== this.original.extractEmbeddedImages) {
            return true;
        }

        if (this.model.extractEmbeddedImages) {
            if (this.model.embeddedImageWidth?.toString() !== this.original.embeddedImageWidth?.toString()) {
                return true;
            }

            if (this.model.embeddedImageHeight?.toString() !== this.original.embeddedImageHeight?.toString()) {
                return true;
            }
        }

        if (this.model.extractCustomEmailHeaders !== this.original.extractCustomEmailHeaders) {
            return true;
        }

        // named query objects
        // handled outside of here

        return dirty;
    }
}

